import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';
import { buildArticleSchema } from '../lib/seo';

const GUIDE_PATH = '/how-to-check-wcag-compliance';
const GUIDE_DATE = '2026-05-21';

interface GuideSection {
  title: string;
  body: string;
  points?: string[];
}

interface GuideStep {
  title: string;
  body: string;
}

interface GuideAction {
  href: string;
  label: string;
  variant: 'primary' | 'secondary' | 'ghost';
}

export default function WcagComplianceGuidePage() {
  const { t } = useTranslation('marketing');
  const wcagPrinciples = t('pages.wcagComplianceGuide.wcag.points', { returnObjects: true }) as string[];
  const checklist = t('pages.wcagComplianceGuide.checklist.items', { returnObjects: true }) as string[];
  const steps = t('pages.wcagComplianceGuide.process.steps', { returnObjects: true }) as GuideStep[];
  const sections = t('pages.wcagComplianceGuide.sections', { returnObjects: true }) as GuideSection[];
  const sidebarPoints = t('pages.wcagComplianceGuide.sidebar.points', { returnObjects: true }) as string[];
  const ctaActions = t('pages.wcagComplianceGuide.cta.actions', { returnObjects: true }) as GuideAction[];

  return (
    <PublicContentPage
      metaTitle={t('pages.wcagComplianceGuide.meta.title')}
      metaDescription={t('pages.wcagComplianceGuide.meta.description')}
      canonicalPath={GUIDE_PATH}
      eyebrow={t('pages.wcagComplianceGuide.hero.eyebrow')}
      title={t('pages.wcagComplianceGuide.hero.title')}
      intro={t('pages.wcagComplianceGuide.hero.intro')}
      metaType="article"
      structuredData={[
        buildArticleSchema({
          headline: t('pages.wcagComplianceGuide.hero.title'),
          description: t('pages.wcagComplianceGuide.meta.description'),
          path: GUIDE_PATH,
          datePublished: GUIDE_DATE,
          dateModified: GUIDE_DATE,
        }),
      ]}
      aside={(
        <>
          <section className="acc-public-page-card acc-public-page-card--sticky">
            <h2>{t('pages.wcagComplianceGuide.sidebar.title')}</h2>
            <p>{t('pages.wcagComplianceGuide.sidebar.body')}</p>
            <ul className="acc-public-page-list">
              {sidebarPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>

          <section className="acc-public-page-card">
            <h2>{t('pages.wcagComplianceGuide.sidebar.noteTitle')}</h2>
            <p>{t('pages.wcagComplianceGuide.sidebar.noteBody')}</p>
          </section>
        </>
      )}
    >
      <section className="acc-public-page-card">
        <h2>{t('pages.wcagComplianceGuide.intro.title')}</h2>
        <p>{t('pages.wcagComplianceGuide.intro.body')}</p>
        <p>{t('pages.wcagComplianceGuide.intro.reference')}</p>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.wcagComplianceGuide.wcag.title')}</h2>
        <p>{t('pages.wcagComplianceGuide.wcag.body')}</p>
        <ul className="acc-public-page-list">
          {wcagPrinciples.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.wcagComplianceGuide.checklist.title')}</h2>
        <ul className="acc-public-page-list" aria-label={t('pages.wcagComplianceGuide.checklist.ariaLabel')}>
          {checklist.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-section" aria-labelledby="wcag-process-title">
        <div>
          <h2 id="wcag-process-title">{t('pages.wcagComplianceGuide.process.title')}</h2>
          <p>{t('pages.wcagComplianceGuide.process.body')}</p>
        </div>

        <div className="acc-public-page-grid">
          {steps.map((step, index) => (
            <article key={step.title} className="acc-public-page-subcard">
              <h3>
                {t('pages.wcagComplianceGuide.process.stepLabel', { number: index + 1 })}
                {' '}
                {step.title}
              </h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
      </section>

      {sections.map((section) => (
        <section key={section.title} className="acc-public-page-card">
          <h2>{section.title}</h2>
          <p>{section.body}</p>
          {section.points && section.points.length > 0 && (
            <ul className="acc-public-page-list">
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <section className="acc-public-page-card">
        <h2>{t('pages.wcagComplianceGuide.disclaimer.title')}</h2>
        <p>{t('pages.wcagComplianceGuide.disclaimer.body')}</p>
      </section>

      <section className="acc-public-cta-band" aria-labelledby="wcag-guide-cta-title">
        <div>
          <h2 id="wcag-guide-cta-title">{t('pages.wcagComplianceGuide.cta.title')}</h2>
          <p>{t('pages.wcagComplianceGuide.cta.body')}</p>
        </div>
        <div className="acc-public-cta-actions">
          {ctaActions.map((action) => (
            <PublicCtaLink key={action.href} href={action.href} variant={action.variant}>
              {action.label}
            </PublicCtaLink>
          ))}
        </div>
      </section>
    </PublicContentPage>
  );
}
